import React, { useEffect, useRef } from "react";
import { DockviewApi } from "dockview-react";
import { api } from "./utils/api";
import { formatIsoTimestampNYC } from "./utils/utils";
import { TickerLink } from "./TickerActionMenu";

interface ITickerChange {
  change_rel: number;
}

interface IPushoverFeedItem {
  id: number;
  created_at: string;
  title: string;
  content: string;
  tickers: string;
  ticker_changes?: Record<string, ITickerChange>;
}

interface ITickerTweet {
  url: string;
  source: string;
  content: string;
  tickers: string;
  published_at: number;
}

const CHART_UP_COLOR = "#0a0a0a";
const CHART_DOWN_COLOR = "#0a0a0a";
const NEW_ITEM_BG = "#fffde7";

type ActiveTab = "pushover" | "tick_tweets";

interface PushoverFeedSectionProps {
  containerApi?: DockviewApi;
}

export const PushoverFeedSection = ({ containerApi }: PushoverFeedSectionProps) => {
  const [activeTab, setActiveTab] = React.useState<ActiveTab>("pushover");
  const [messages, setMessages] = React.useState<IPushoverFeedItem[]>([]);
  const [tweets, setTweets] = React.useState<ITickerTweet[]>([]);
  const [newMessageIds, setNewMessageIds] = React.useState<Set<number>>(new Set());
  const [newTweetUrls, setNewTweetUrls] = React.useState<Set<string>>(new Set());
  const knownMessageIds = useRef<Set<number>>(new Set());
  const knownTweetUrls = useRef<Set<string>>(new Set());
  const initialLoadDone = useRef({ messages: false, tweets: false });

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchTweets();
    const interval = setInterval(fetchTweets, 5000);
    return () => clearInterval(interval);
  }, []);

  function fetchMessages() {
    api
      .get<IPushoverFeedItem[]>("/pushover-feed")
      .then((response) => {
        const data = response.data;
        if (!initialLoadDone.current.messages) {
          initialLoadDone.current.messages = true;
          knownMessageIds.current = new Set(data.map((m) => m.id));
        } else {
          const fresh = data.filter((m) => !knownMessageIds.current.has(m.id));
          if (fresh.length > 0) {
            setNewMessageIds((prev) => {
              const next = new Set(prev);
              fresh.forEach((m) => next.add(m.id));
              return next;
            });
            fresh.forEach((m) => knownMessageIds.current.add(m.id));
          }
        }
        setMessages(data);
      })
      .catch((error) => {
        console.error("Error fetching pushover feed:", error);
      });
  }

  function fetchTweets() {
    api
      .get<ITickerTweet[]>("/ticker-tweets")
      .then((response) => {
        const data = response.data;
        if (!initialLoadDone.current.tweets) {
          initialLoadDone.current.tweets = true;
          knownTweetUrls.current = new Set(data.map((t) => t.url));
        } else {
          const fresh = data.filter((t) => !knownTweetUrls.current.has(t.url));
          if (fresh.length > 0) {
            setNewTweetUrls((prev) => {
              const next = new Set(prev);
              fresh.forEach((t) => next.add(t.url));
              return next;
            });
            fresh.forEach((t) => knownTweetUrls.current.add(t.url));
          }
        }
        setTweets(data);
      })
      .catch((error) => {
        console.error("Error fetching ticker tweets:", error);
      });
  }

  function dismissMessage(id: number) {
    setNewMessageIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }

  function dismissTweet(url: string) {
    setNewTweetUrls((prev) => {
      const next = new Set(prev);
      next.delete(url);
      return next;
    });
  }

  function formatUnixTs(ts: number): string {
    const ms = ts < 1e12 ? ts * 1000 : ts;
    return new Date(ms).toLocaleString("en-US", {
      timeZone: "America/New_York",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }

  return (
    <div className="h-full flex flex-col overflow-hidden font-sans">
      <div className="flex-shrink-0 flex items-center gap-4 px-2 py-1.5 border-b border-gray-200">
        <form onSubmit={(e) => e.preventDefault()} className="flex items-center gap-4">
          <label className="flex items-center gap-1.5 cursor-pointer select-none text-sm">
            <input
              type="radio"
              name="timeline-tab"
              value="pushover"
              checked={activeTab === "pushover"}
              onChange={() => setActiveTab("pushover")}
            />
            Pushover feed
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer select-none text-sm">
            <input
              type="radio"
              name="timeline-tab"
              value="tick_tweets"
              checked={activeTab === "tick_tweets"}
              onChange={() => setActiveTab("tick_tweets")}
            />
            TICK Tweets
          </label>
        </form>
      </div>

      <div className={`flex-1 overflow-auto ${activeTab !== "pushover" ? "hidden" : ""}`}>
        {messages.length === 0 && (
          <p className="text-sm text-gray-500 p-2">No notifications yet.</p>
        )}
        {messages.map((message) => (
          <div
            key={message.id}
            className="p-2"
            style={newMessageIds.has(message.id) ? { backgroundColor: NEW_ITEM_BG } : undefined}
            onMouseEnter={newMessageIds.has(message.id) ? () => dismissMessage(message.id) : undefined}
          >
            <p className="text-sm text-gray-600">{formatIsoTimestampNYC(message.created_at)}</p>
            <p>
              {message.tickers &&
                message.tickers.split(" ").filter(Boolean).map((ticker) => {
                  const c = message.ticker_changes?.[ticker];
                  return (
                    <span key={ticker} className="mr-1">
                      <TickerLink ticker={ticker} containerApi={containerApi} className="font-bold cursor-pointer" />
                      {c && (
                        <span
                          className="ml-1 font-semibold tabular-nums"
                          style={{ color: c.change_rel >= 0 ? CHART_UP_COLOR : CHART_DOWN_COLOR }}
                        >
                          {c.change_rel >= 0 ? "+" : ""}
                          {c.change_rel.toFixed(1)}%
                        </span>
                      )}
                    </span>
                  );
                })}
              <span className="font-normal text-gray-500">{message.title}</span>
            </p>
            <p className="whitespace-pre-wrap font-normal text-black pl-6">{message.content}</p>
          </div>
        ))}
      </div>

      <div className={`flex-1 overflow-auto ${activeTab !== "tick_tweets" ? "hidden" : ""}`}>
        {tweets.length === 0 && (
          <p className="text-sm text-gray-500 p-2">No ticker tweets yet.</p>
        )}
        {tweets.map((tweet) => (
          <div
            key={tweet.url}
            className="p-2"
            style={newTweetUrls.has(tweet.url) ? { backgroundColor: NEW_ITEM_BG } : undefined}
            onMouseEnter={newTweetUrls.has(tweet.url) ? () => dismissTweet(tweet.url) : undefined}
          >
            <p className="text-sm text-gray-600">
              <span className="font-semibold">{tweet.source}</span>
              {" · "}
              {formatUnixTs(tweet.published_at)}
            </p>
            <p>
              {tweet.tickers.split(" ").filter(Boolean).map((ticker) => (
                <TickerLink key={ticker} ticker={ticker} containerApi={containerApi} className="font-bold cursor-pointer mr-1" />
              ))}
            </p>
            <p className="whitespace-pre-wrap font-normal text-black pl-6">{tweet.content}</p>
          </div>
        ))}
      </div>

    </div>
  );
};
